import React, { useState, useRef, useEffect } from "react";
import { Typography, Box, IconButton, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const ALLOWED_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGES = 15;

const useStyles = makeStyles((theme) => ({
  dropzone: {
    border: "2px dashed #ccc",
    textAlign: "center",
    cursor: "pointer",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "#fff",
    transition: "background-color 0.3s, border-color 0.3s, border-style 0.3s",
    "&:hover": {
      borderColor: "#2196F3",
      backgroundColor: "#2196F314",
    },
    height: "120px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  dropzoneError: {
    border: "2px solid #D32F2F",
    backgroundColor: "#D32F2F0A",
  },
  fileItem: {
    position: "relative",
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #ccc",
    backgroundColor: "#fff",
  },
  imagePreview: {
    width: "50px",
    height: "50px",
    borderRadius: theme.shape.borderRadius,
  },
  deleteButton: {
    position: "absolute",
    top: 5,
    right: 5,
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "#f44336",
    },
  },
}));

const Dropzone = ({
  files = [],
  setFiles,
  setError,
  error,
  initialUrls = [],
  onDelete,
  isDisabled = false,
  maxImages = MAX_IMAGES,
  isModalClosing = false,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [isError, setIsError] = useState(false);
  const [imageSizes, setImageSizes] = useState({});

  useEffect(() => {
    if (initialUrls.length > 0) {
      const urlFiles = initialUrls.map((url) => ({
        file: null,
        url: url.picture,
        id: url.id,
        status: "complete",
      }));
      setFiles(urlFiles);
    }
  }, [initialUrls, setFiles]);

  useEffect(() => {
    if (isModalClosing) return; // 🚫 ป้องกัน revoke ตอน modal ปิดระหว่าง render

    return () => {
      if (files && Array.isArray(files)) {
        files.forEach((fileObj) => {
          if (
            fileObj?.file instanceof File &&
            fileObj?.url?.startsWith("blob:")
          ) {
            try {
              URL.revokeObjectURL(fileObj.url);
            } catch (e) {
              console.warn("Revoke object URL failed:", e);
            }
          }
        });
      }
    };
  }, [files, isModalClosing]);

  const handleDrop = (event) => {
    event.preventDefault();
    const newFiles = Array.from(event.dataTransfer.files);
    handleFiles(newFiles);
  };

  const handleInputChange = (event) => {
    const newFiles = Array.from(event.target.files);
    handleFiles(newFiles);
    event.target.value = "";
  };

  const handleFiles = (newFiles) => {
    const validFiles = newFiles.filter((file) =>
      ALLOWED_FILE_TYPES.includes(file.type)
    );

    if (validFiles.length !== newFiles.length) {
      setError("Only JPG, JPEG, or PNG files are allowed.");
      setIsError(true);
    } else {
      const sizeFilteredFiles = validFiles.filter(
        (file) => file.size <= 3 * 1024 * 1024
      ); // 3 MB
      if (sizeFilteredFiles.length !== validFiles.length) {
        setError("Each file must not exceed 3 MB.");
        setIsError(true);
        return;
      }

      setError(null);
      setIsError(false);

      const totalFiles = (files?.length || 0) + validFiles.length;

      if (totalFiles > maxImages) {
        setError(`You can only upload a maximum of ${maxImages} images.`);
        setIsError(true);
        return;
      }

      const updatedFiles = [...(files || [])];
      validFiles.forEach((file) => {
        let previewUrl = "";
        try {
          if (file instanceof File) {
            previewUrl = URL.createObjectURL(file);
          }
        } catch (err) {
          console.warn("createObjectURL failed:", err);
        }

        updatedFiles.push({
          file,
          url: previewUrl,
          id: null,
          status: "complete",
        });
      });

      setFiles(updatedFiles);
    }
  };

  const fetchImageSize = async (url, index) => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      const sizeInBytes = response.headers.get("content-length");
      if (sizeInBytes) {
        const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2); // Convert to MB
        setImageSizes((prevSizes) => ({
          ...prevSizes,
          [index]: `${sizeInMB} MB`,
        }));
      }
    } catch (error) {
      console.error("Error fetching image size:", error);
      setImageSizes((prevSizes) => ({
        ...prevSizes,
        [index]: "Unknown size",
      }));
    }
  };

  useEffect(() => {
    if (isModalClosing) return; // 🚫 ถ้า modal กำลังปิด หยุดทำงาน

    let isMounted = true;
    files?.forEach((fileObj, index) => {
      if (
        isMounted &&
        fileObj &&
        (fileObj.url ?? fileObj) &&
        !imageSizes[index]
      ) {
        const urlToCheck = typeof fileObj === "string" ? fileObj : fileObj.url;
        if (urlToCheck?.startsWith("blob:") || urlToCheck?.startsWith("http")) {
          fetchImageSize(urlToCheck, index);
        }
      }
    });

    return () => {
      isMounted = false;
    };
  }, [files, imageSizes, isModalClosing]);

  const handleDelete = (index, id) => {
    if (onDelete) {
      onDelete(index, id ?? null);
    }
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };

  return (
    <>
      <Box
        className={`${classes.dropzone} ${
          isError ? classes.dropzoneError : ""
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current.click()}
      >
        <UploadFileIcon fontSize="large" />
        <Typography variant="body2">
          <span style={{ textDecoration: "underline", color: "#2196F3" }}>
            Choose file
          </span>{" "}
          or drag and drop
        </Typography>
        <Typography variant="body2" color="textSecondary">
          PNG, JPG or JPEG (Max 3MB)
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleInputChange}
          multiple
          accept=".jpg,.jpeg,.png"
          disabled={isDisabled}
        />
      </Box>

      {Array.isArray(files) && files.length > 0 && (
        <Grid container spacing={1} sx={{ mt: 1 }}>
          {(files || []).map((fileObj, index) => (
            <Grid item xs={12} key={index}>
              <Box
                className={classes.fileItem}
                display="flex"
                alignItems="center"
              >
                {fileObj?.url ? (
                  <img
                    src={fileObj?.url}
                    alt="Preview"
                    className={classes.imagePreview}
                  />
                ) : fileObj ? (
                  <img
                    src={fileObj} // For new files
                    alt="Preview"
                    className={classes.imagePreview}
                  />
                ) : null}
                <Box sx={{ ml: 1 }}>
                  {fileObj?.file
                    ? fileObj.file?.name
                    : typeof fileObj === "string"
                    ? fileObj?.split("/").pop()
                    : fileObj?.name
                    ? fileObj.name
                    : "Existing Image"}
                  <Typography variant="body2">
                    {fileObj.file
                      ? `${(fileObj.file.size / (1024 * 1024)).toFixed(2)} MB`
                      : fileObj.size
                      ? `${(fileObj.size / (1024 * 1024)).toFixed(2)} MB`
                      : imageSizes[index] || "Loading size..."}
                    {fileObj.status === "complete" && " • Complete"}
                  </Typography>
                </Box>
                {!isDisabled && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(index, fileObj.id || null)}
                    className={classes.deleteButton}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export default Dropzone;
